import Stack, { StackProps } from '@mui/material/Stack';

import styled from 'shared/utils/styled';

export const CaseContainer = styled((props: StackProps) => (
  <Stack spacing={4} {...props} />
))({});

export const CaseTitle = styled('h2')(({ theme }) => ({
  margin: 0,
  padding: 0,

  fontSize: theme.typography.pxToRem(28),
  fontWeight: 600,
  color: theme.palette.primary.main,

  [theme.breakpoints.down('md')]: {
    fontSize: '1.4rem',
  },
}));

export const CardsContainer = styled((props: StackProps) => (
  <Stack
    alignItems="flex-start"
    direction={{
      md: 'row',
      xs: 'column',
    }}
    spacing={{
      md: 3.5,
      xs: 2,
    }}
    {...props}
  />
))({
  width: '100%',
});

export const BorderedCardsContainer = styled(CardsContainer)(({ theme }) => ({
  padding: '15px 25px',
  background: '#fff',
  borderLeft: `5px solid ${theme.palette.primary.main}`,
  borderRadius: 2,
  [theme.breakpoints.down('sm')]: {
    padding: 10,
  },
}));

export const CardsColumn = styled((props: StackProps) => (
  <Stack
    direction="column"
    maxWidth={{
      md: '50%',
      xs: '100%',
    }}
    spacing={3.5}
    width="100%"
    {...props}
  />
))({});

export const CardsRow = styled((props: StackProps) => (
  <Stack
    alignItems="flex-start"
    direction={{
      xs: 'column',
      md: 'row',
    }}
    spacing={3.5}
    width="100%"
    {...props}
  />
))({});

export const CardContainer = styled((props: StackProps) => (
  <Stack spacing="10px" {...props} />
))({ width: '100%' });

export const CardTitle = styled('h3')(({ theme }) => ({
  margin: 0,
  padding: 0,

  fontSize: theme.typography.pxToRem(16),
  fontWeight: 400,
  color: theme.palette.primary.main,
}));

export const Card = styled((props: StackProps) => (
  <Stack spacing={4} {...props} />
))(({ theme }) => ({
  padding: '20px 15px',
  background: '#F2F4F8',
  [theme.breakpoints.down('sm')]: {
    padding: '15px 10px',
  },
}));

export const ListContainer = styled((props: StackProps) => (
  <Stack spacing="10px" {...props} />
))(() => ({}));

export const List = styled((props: StackProps<'ul'>) => (
  <Stack component="ul" spacing="10px" {...props} />
))(() => ({
  margin: 0,
  padding: 0,
  listStyle: 'none',
}));

export const ListTitle = styled('h4')(({ theme }) => ({
  margin: 0,
  padding: 0,

  fontSize: 15,
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

export const Item = styled((props: StackProps<'li'>) => (
  <Stack direction="row" component="li" spacing={2} {...props} />
))(() => ({}));

export const Label = styled('span')(() => ({
  flex: 1,
  flexBasis: '50%',
  fontSize: 14,
  color: '#646464',
  wordBreak: 'break-word',
}));

type ValueAccessories = Partial<{
  /**
   * @default 'right'
   */
  align: 'left' | 'center' | 'right';
  fullWidth: boolean;
}>;

export const Value = styled('span')<ValueAccessories>(
  ({ align = 'right', fullWidth }) => ({
    display: 'flex',
    flex: 1,
    flexBasis: '50%',
    justifyContent: 'flex-end',

    fontSize: 14,
    color: '#224060',
    textAlign: 'right',
    wordBreak: 'break-word',

    ...(align === 'left' && {
      justifyContent: 'flex-start',
      textAlign: 'left',
    }),
    ...(align === 'center' && {
      justifyContent: 'center',
      textAlign: 'center',
    }),
    ...(fullWidth && {
      flexBasis: '100%',
    }),
  })
);

const CaseHeader = styled('h2')(({ theme }) => ({
  margin: 0,
  padding: 0,

  fontSize: theme.typography.pxToRem(21),
  fontWeight: 400,
  color: '#444',
}));

const CaseStatus = styled('div')(({ theme }) => ({
  padding: '5px 10px',

  fontSize: theme.typography.pxToRem(11),
  background: '#42464d',
  borderRadius: 2,
  color: '#fff',
  textTransform: 'uppercase',
}));

type CaseTitleProps = {
  status: string | null;
  title: string;
};

export const CaseInfoTitle = ({ status, title }: CaseTitleProps) => (
  <Stack alignItems="center" direction="row" spacing={2}>
    <CaseHeader>{title}</CaseHeader>
    {status && <CaseStatus>{status}</CaseStatus>}
  </Stack>
);
